import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { MarketingSettings } from "../../types/marketing";
export function SocialAccountReadiness({
  accounts,
  settings,
  onManage,
  onSettings,
}: {
  accounts: Array<{
    id: number;
    account_name: string;
    platform_display: string;
    connection_status?: string;
    is_active: boolean;
  }>;
  settings: MarketingSettings | null | undefined;
  onManage: () => void;
  onSettings: () => void;
}) {
  const client = useQueryClient();
  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch(`/api/marketing/settings/${settings!.id}/`, {
        social_posting_enabled: enabled,
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["marketing-settings"] }),
  });
  const active = accounts.filter(
    (a) => a.is_active && a.connection_status !== "reconnect",
  );
  return (
    <Card variant="soft" sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography level="title-lg">Publishing readiness</Typography>
          <Typography level="body-sm">
            Check accounts and publishing settings before filling your calendar.
          </Typography>
        </Box>
        <Button size="sm" variant="outlined" onClick={onManage}>
          Manage accounts
        </Button>
      </Box>
      {!accounts.length && (
        <Alert color="warning">
          Connect an account to start scheduling posts.
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {accounts.map((account) => (
          <Chip
            sx={{
              maxWidth: "100%",
              height: "auto",
              "& span": { whiteSpace: "normal", overflowWrap: "anywhere" },
            }}
            key={account.id}
            variant="outlined"
            color={
              account.connection_status === "connected" ? "success" : "warning"
            }
          >
            {account.account_name} · {account.platform_display} ·{" "}
            {account.connection_status === "refresh_due"
              ? "Token refresh needed"
              : account.connection_status === "connected"
                ? "Connected"
                : account.is_active
                  ? "Reconnect needed"
                  : "Disconnected"}
          </Chip>
        ))}
      </Box>
      <Typography level="body-xs">
        Connection labels reflect saved credentials. Posting permissions are
        checked at publication time.
      </Typography>
      <Box
        sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}
      >
        <Stack spacing={0.5}>
          <Switch
            checked={Boolean(settings?.social_posting_enabled)}
            disabled={!settings || toggle.isPending}
            onChange={(e) => toggle.mutate(e.target.checked)}
            endDecorator="Automatic publishing"
            slotProps={{ input: { "aria-label": "Automatic publishing" } }}
          />
          <Typography level="body-xs">
            Publishes posts you have explicitly scheduled. Drafts remain
            private.
          </Typography>
        </Stack>
        <Stack spacing={0.5}>
          <Typography level="title-sm">
            AI generation: {settings?.social_auto_generate ? "On" : "Off"}
          </Typography>
          <Button size="sm" variant="plain" onClick={onSettings}>
            Generation settings
          </Button>
        </Stack>
      </Box>
      {settings && !settings.social_posting_enabled && (
        <Alert color="warning">
          Publishing is paused. Scheduled posts will remain queued until
          automatic publishing is enabled.
        </Alert>
      )}
      {!!accounts.length && !active.length && (
        <Alert color="warning">
          No active destination is ready. Reconnect an account before
          scheduling.
        </Alert>
      )}
      {toggle.isError && (
        <Alert color="danger">
          Could not save publishing settings. Your previous setting remains in
          effect; try again.
        </Alert>
      )}
    </Card>
  );
}
